import React from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import {
  BlueButton,
  CustomSelect,
  EmptyFilterMessage,
  EmptySearchMessage,
  FilterCheckbox,
  FilterDistance,
  FilterStars,
  SearchPropertyName,
  SkeletonContent,
  WhiteButton,
  SkeletonMap,
  SessionExpiredMessage,
} from 'src/components/components/index';
import {
  filtersActions,
  resetHotels,
  getHotels,
  getMapHotels,
  hotelsActions,
  IDatesState,
  IFiltersState,
  IHotelsState,
  ILocationsState,
  IRoomsState,
  locationActions,
} from 'src/store/slices/index';
import {
  CloseBlueSvg,
  FilterSvg,
  MapSvg,
  PaperMapSvg,
  ListViewSvg,
  CloseSvg,
} from 'src/assets/index';
import { RootState } from 'src/store';
import {
  IBounds,
  ICounterItem,
  IHotel,
  ILocation,
  IRange,
  IRoom,
  LocationsTypes,
} from 'src/common-types/index';
import MapImage from 'src/assets/images/map.jpg';
import {
  APP_SELECTOR,
  DEFAULT_PAGE_SIZE,
  FILTERS_LABEL,
  SortTypes,
  TOP_AMENITIES_ICONS,
} from 'src/constants/index';
import {
  getAmenities,
  isFiltersChanged,
  isFiltersEmpty,
  UrlUtils,
  Responsive,
  Map,
  isDataValid,
  getSortOptions,
} from 'src/utils/index';
import { HotelCardComponent } from '../hotel-card';
import { MapWrapper } from '../../map';
import './style.scss';

interface IMapStateToProps {
  hotelsStore: IHotelsState;
  datesStore: IDatesState;
  locationsStore: ILocationsState;
  roomsStore: IRoomsState;
  filtersStore: IFiltersState;
}

interface IMapDispatchToProps {
  setPageNumber: () => void;
  getHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    isLoadMore: boolean,
    isFiltersChange: boolean,
  ) => void;
  resetHotels: () => void;
  setBudget: (value: string[]) => void;
  setMapLoading: (isMapLoading: boolean) => void;
  setAmenities: (value: string[]) => void;
  setAccommodationType: (value: string[]) => void;
  setNeighbourhoods: (value: string[]) => void;
  setPlaces: (value: string[]) => void;
  setStarRating: (value: number[]) => void;
  setDistance: (value: number) => void;
  setSortBy: (value: string) => void;
  resetFilters: () => void;
  getMapHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    hotelId?: number,
  ) => void;
  setIsMapMessageShown: (shown: boolean) => void;
  validateLocation: (error: string) => void;
  setBounds: (bounds: IBounds) => void;
  setIsMapView: (isMapView: boolean) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, WrappedComponentProps {
  label: string;
  matches?: boolean;
}

interface IState {
  isFullAmenities: boolean;
  isFullNeighbourhoods: boolean;
  isMobileFilter: boolean;
  isMapView: boolean;
  selectedHotel: IHotel;
}

const debounceTime = 300;
const scrollPixels = 350;
const zero = 0;
const maxAmenitiesCount = 6;
const plainOptionsStar = [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }];

class ResultWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    isFullAmenities: false,
    isFullNeighbourhoods: false,
    isMobileFilter: false,
    isMapView: false,
    selectedHotel: null,
  };
  root: HTMLDivElement = document.querySelector(APP_SELECTOR);
  prevFilters: IFiltersState = null;

  checkIsLocationValid = (): boolean => {
    const { datesStore, locationsStore, validateLocation, intl } = this.props;
    const { isLocationValid } = isDataValid(locationsStore, datesStore);

    if (!isLocationValid) {
      validateLocation(intl.formatMessage({ id: 'location.error' }));
      return false;
    }

    return true;
  };

  onScroll = debounce(() => {
    const { setPageNumber, getHotels, roomsStore, datesStore, locationsStore, hotelsStore } =
      this.props;
    const { loadingMore, hotels, prevHotels, isSessionExpired, lastSearchedLocation } = hotelsStore;
    const { selectedLocation } = locationsStore;
    const { startDate, endDate } = datesStore;
    const { rooms } = roomsStore;

    if (!lastSearchedLocation && !selectedLocation) {
      this.checkIsLocationValid();
    }

    if (
      !isSessionExpired &&
      hotels &&
      hotels.length &&
      hotels.length % DEFAULT_PAGE_SIZE === zero &&
      !loadingMore &&
      prevHotels.length &&
      this.root &&
      window.innerHeight + window.scrollY + scrollPixels >= this.root.offsetHeight
    ) {
      setPageNumber();
      getHotels(
        lastSearchedLocation ? lastSearchedLocation : selectedLocation,
        startDate,
        endDate,
        rooms,
        true,
        false,
      );
    }
  }, debounceTime);

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    const footer: HTMLDivElement = document.querySelector('.footer');

    if (footer) {
      document.body.style.overflow = 'auto';
      footer.style.display = 'block';
    }
  }

  onBudgetChange = (checkedValues: string[]) => {
    this.props.setBudget(checkedValues);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onAmenitiesChange = (checkedValues: string[]) => {
    this.props.setAmenities(checkedValues);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onAccommodationTypeChange = (checkedValues: string[]) => {
    this.props.setAccommodationType(checkedValues);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onNeighborhoodsChange = (checkedValues: string[]) => {
    this.props.setNeighbourhoods(checkedValues);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  getBudgetLabel = (range: IRange) => {
    const { from, to } = range;

    if (from && to) {
      return (
        <span>
          ${from} <FormattedMessage id="to" /> ${to}
        </span>
      );
    }

    if (!from && to) {
      return (
        <span>
          <FormattedMessage id="less.than" /> ${to}
        </span>
      );
    }

    if (from && !to) {
      return (
        <span>
          <FormattedMessage id="greater.than" /> ${from}
        </span>
      );
    }
  };

  toggleAmenities = () => {
    this.setState(({ isFullAmenities }) => ({
      isFullAmenities: !isFullAmenities,
    }));
  };

  toggleNeighbourhoods = () => {
    this.setState(({ isFullNeighbourhoods }) => ({
      isFullNeighbourhoods: !isFullNeighbourhoods,
    }));
  };

  getSortOptions = (): LabeledValue[] => {
    return getSortOptions();
  };

  onSearchSave = () => {
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onDistanceChange = (value: number) => {
    this.props.setDistance(value);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onStarsChange = (value: number[]) => {
    this.props.setStarRating(value);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onSortChange = (value: SelectValue) => {
    this.props.setSortBy(value as string);
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onResetFilters = (fromMap = false) => {
    UrlUtils.setUrl(FILTERS_LABEL, null);

    if (fromMap) {
      this.props.setMapLoading(fromMap);
    }

    this.props.resetFilters();
    return !this.state.isMobileFilter && this.onFiltersOrSortChange();
  };

  onFiltersOrSortChange = debounce(() => {
    const {
      getHotels,
      locationsStore,
      datesStore,
      roomsStore,
      filtersStore,
      hotelsStore,
      resetHotels,
    } = this.props;
    const { lastSearchedLocation } = hotelsStore;
    const { selectedLocation } = locationsStore;
    const { startDate, endDate } = datesStore;
    const { rooms } = roomsStore;

    if (!lastSearchedLocation && !selectedLocation) {
      this.checkIsLocationValid();
    }

    if (isFiltersChanged(this.prevFilters, filtersStore)) {
      UrlUtils.setUrl(FILTERS_LABEL, filtersStore);
      this.prevFilters = filtersStore;
      resetHotels();
      getHotels(
        lastSearchedLocation ? lastSearchedLocation : selectedLocation,
        startDate,
        endDate,
        rooms,
        true,
        true,
      );
    }
  }, debounceTime);

  onMobileFilter = () => {
    if (!this.props.hotelsStore.isSessionExpired) {
      this.setState({ isMobileFilter: !this.state.isMobileFilter });
      return this.state.isMobileFilter
        ? (document.body.style.overflow = 'auto')
        : (document.body.style.overflow = 'hidden');
    }
  };

  onApply = () => {
    this.onMobileFilter();
    this.onFiltersOrSortChange();
  };

  showMap = (center: google.maps.LatLngLiteral, selectedHotel: IHotel = null): void => {
    if (!this.props.hotelsStore.isSessionExpired) {
      const footer: HTMLDivElement = document.querySelector('.footer');

      this.setState({ isMapView: true, selectedHotel });
      this.props.setIsMapView(true);
      this.onMapSearchUpdate(selectedHotel);

      if (footer) {
        window.scrollTo(zero, zero);
        document.body.style.overflow = 'hidden';
        footer.style.display = 'none';
      }
    }
  };

  showListView = (): void => {
    if (!this.props.hotelsStore.isSessionExpired) {
      const footer: HTMLDivElement = document.querySelector('.footer');

      this.setState({ isMapView: false });
      this.props.setIsMapView(false);

      if (footer) {
        document.body.style.overflow = 'auto';
        footer.style.display = 'block';
      }
    }
  };

  onCloseMessage = () => {
    this.props.setIsMapMessageShown(false);
  };

  onMapSearchUpdate = (selectedHotel: IHotel = null): void => {
    if (!this.props.hotelsStore.isSessionExpired) {
      const { getMapHotels, datesStore, roomsStore, locationsStore } = this.props;
      const { selectedLocation } = locationsStore;
      const { startDate, endDate } = datesStore;
      const { rooms } = roomsStore;

      getMapHotels(selectedLocation, startDate, endDate, rooms, selectedHotel?.hotelId);
    }
  };

  render(): React.ReactNode {
    const { isFullAmenities, isMobileFilter, isMapView, selectedHotel, isFullNeighbourhoods } =
      this.state;
    const { hotelsStore, label, filtersStore, locationsStore, matches, setBounds } = this.props;
    const {
      hotels,
      searchedLocation,
      lastSearchedLocation,
      loadingMore,
      counters,
      mapCounters,
      suppliers,
      loadingFilters,
      mapHotels,
      bounds,
      isMapMessageShown,
      isSessionExpired,
      mapLoading,
    } = hotelsStore;
    const {
      amenities,
      accommodationType,
      budget,
      neighbourhoods,
      distance,
      propertyName,
      sortBy,
      starRatings,
    } = filtersStore;
    const { selectedLocation } = locationsStore;
    const resultCounters = isMapView ? (mapCounters ? mapCounters : counters) : counters;

    if (!resultCounters) {
      return null;
    }

    const sortedAmenities = getAmenities(resultCounters.amenities);
    const amenitiesToShow: ICounterItem[] = isFullAmenities
      ? sortedAmenities
      : sortedAmenities.slice(zero, maxAmenitiesCount);
    const neighbourhoodsToShow: ICounterItem[] = isFullNeighbourhoods
      ? resultCounters.neighbourhoods
      : resultCounters.neighbourhoods.slice(zero, maxAmenitiesCount);
    const isShowAmenities = !!amenitiesToShow.filter(({ count }) => !!count).length;
    const isShowNeighbourhoods = !!resultCounters.neighbourhoods.filter(({ count }) => !!count)
      .length;
    const isShowBudgets = !!resultCounters.budgetRanges.filter(({ count }) => !!count).length;
    const isShowPropTypes = !!resultCounters.propertyTypes.filter(({ count }) => !!count).length;
    const applyFilters = [
      amenities,
      accommodationType,
      budget,
      neighbourhoods,
      distance,
      propertyName,
      sortBy,
      starRatings,
    ].filter(String);

    return (
      <div className={`result-wrapper ${isMapView ? 'map-view' : ''}`}>
        <div className="result-wrapper__wrapper">
          <div
            className={`result-wrapper__filters-wrapper ${isMobileFilter ? 'is-mobile' : ''} ${
              isSessionExpired ? 'disabled' : ''
            }`}
          >
            {isMapView ? (
              <div className="result-wrapper__list-view-wrapper" onClick={this.showListView}>
                <ListViewSvg /> <FormattedMessage id="view.list" />
              </div>
            ) : (
              <div
                className="result-wrapper__map-view-wrapper"
                onClick={() => this.showMap(Map.getGoogleLocation(selectedLocation.geoLocation))}
              >
                <div
                  className="result-wrapper__map-view"
                  style={{ backgroundImage: `url(${MapImage})` }}
                >
                  <div className="result-wrapper__map-button">
                    <PaperMapSvg /> <FormattedMessage id="view.on.map" />
                  </div>
                  <div className="result-wrapper__map-gradient" />
                </div>
              </div>
            )}
            <div className="result-wrapper__filters-header-wrapper">
              <div className="result-wrapper__filters-header-item" onClick={this.onMobileFilter}>
                <CloseBlueSvg />
                <p className="result-wrapper__filters-header-text">
                  <FormattedMessage id="sort.filter" />
                </p>
              </div>
              {!isFiltersEmpty(filtersStore) && (
                <p
                  className="result-wrapper__mobile-reset-filters-link"
                  onClick={() => this.onResetFilters()}
                >
                  <FormattedMessage id="reset.all" />
                </p>
              )}
            </div>
            <div
              className={`result-wrapper__sort-select-wrapper ${
                isSessionExpired ? 'disabled' : ''
              }`}
            >
              <span className="result-wrapper__sort-label">
                <FormattedMessage id="sort.by" />:{' '}
              </span>
              <div className="result-wrapper__select">
                <CustomSelect
                  disabled={isSessionExpired}
                  value={sortBy}
                  onChange={this.onSortChange}
                  options={this.getSortOptions()}
                />
              </div>
            </div>
            {selectedLocation?.locationType !== LocationsTypes.Hotel &&
              (!isMapView || (isMapView && !matches)) && (
                <SearchPropertyName
                  disabled={isSessionExpired}
                  className="top-search-wrapper"
                  onSaveValue={this.onSearchSave}
                  title="search.property.name"
                  isMapView={isMapView}
                />
              )}
            <p className="result-wrapper__filters-tittle">
              <FormattedMessage id="filter.by" />
            </p>
            <FilterDistance
              disabled={isSessionExpired}
              title="filter.search.by.radius"
              onChange={this.onDistanceChange}
              value={distance}
            />
            {isShowNeighbourhoods ? (
              <>
                <FilterCheckbox
                  disabled={isSessionExpired}
                  onChange={this.onNeighborhoodsChange}
                  values={neighbourhoods}
                  options={neighbourhoodsToShow.map(({ count, key }) => {
                    return {
                      count,
                      value: key,
                      label: <span>{key}</span>,
                    };
                  })}
                  title="neighborhoods"
                />
                {!isSessionExpired && resultCounters.neighbourhoods.length > maxAmenitiesCount && (
                  <div className="result-wrapper__show-link" onClick={this.toggleNeighbourhoods}>
                    {isFullNeighbourhoods ? (
                      <FormattedMessage id="show.less" />
                    ) : (
                      <FormattedMessage id="show.more" />
                    )}
                  </div>
                )}
              </>
            ) : null}
            {isShowBudgets ? (
              <FilterCheckbox
                disabled={isSessionExpired}
                onChange={this.onBudgetChange}
                values={budget}
                options={resultCounters.budgetRanges.map(({ count, key }) => {
                  return {
                    count,
                    value: `${key.from}-${key.to}`,
                    label: this.getBudgetLabel(key),
                  };
                })}
                title="budget"
              />
            ) : null}
            <FilterStars
              disabled={isSessionExpired}
              title="star.rating"
              options={plainOptionsStar}
              onChange={this.onStarsChange}
              value={starRatings}
            />
            {isShowAmenities ? (
              <>
                <FilterCheckbox
                  disabled={isSessionExpired}
                  onChange={this.onAmenitiesChange}
                  values={amenities}
                  options={amenitiesToShow.map(({ count, key }) => {
                    const icon = TOP_AMENITIES_ICONS[key.toLowerCase()];

                    return {
                      count,
                      value: key,
                      label: (
                        <div className="filter__icon-item">
                          <span className="filter__icon-wrapper">{icon}</span>
                          <span>{key}</span>
                        </div>
                      ),
                    };
                  })}
                  title="tab.amenities"
                />
                {!isSessionExpired && sortedAmenities.length > maxAmenitiesCount && (
                  <div className="result-wrapper__show-link" onClick={this.toggleAmenities}>
                    {isFullAmenities ? (
                      <FormattedMessage id="show.less" />
                    ) : (
                      <FormattedMessage id="show.more" />
                    )}
                  </div>
                )}
              </>
            ) : null}
            {isShowPropTypes ? (
              <FilterCheckbox
                disabled={isSessionExpired}
                onChange={this.onAccommodationTypeChange}
                values={accommodationType}
                options={resultCounters.propertyTypes.map(({ count, key }) => {
                  return {
                    count,
                    value: key,
                    label: <span>{key}</span>,
                  };
                })}
                title="accommodation.type"
              />
            ) : null}
            {!isFiltersEmpty(filtersStore) && !isSessionExpired && (
              <div className="result-wrapper__reset-filters-wrapper">
                <span
                  className="result-wrapper__reset-filters-link"
                  onClick={() => this.onResetFilters()}
                >
                  <FormattedMessage id="reset.all" />
                </span>
              </div>
            )}
            {!isSessionExpired && (
              <div className="result-wrapper__filters-apply">
                <BlueButton onClick={this.onApply} disabled={isSessionExpired}>
                  <FormattedMessage id="apply" />
                  <span className="result-wrapper__filters-apply-counts">
                    {applyFilters.length}
                  </span>
                  <FormattedMessage id="filters" />
                </BlueButton>
              </div>
            )}
          </div>
          <div className={`result-wrapper__btn-view-wrapper ${isSessionExpired ? 'disabled' : ''}`}>
            <WhiteButton onClick={this.onMobileFilter} disabled={isSessionExpired}>
              <FilterSvg />
              <FormattedMessage id="sort.filter" />
            </WhiteButton>
            <WhiteButton
              disabled={isSessionExpired}
              onClick={() => this.showMap(Map.getGoogleLocation(selectedLocation.geoLocation))}
            >
              <MapSvg />
              <FormattedMessage id="view.map" />
            </WhiteButton>
          </div>
          {isMapView ? (
            <div className="result-wrapper__map-wrapper">
              <div className="result-wrapper__map-top">
                <div className="result-wrapper__map-close" onClick={this.showListView}>
                  <CloseSvg />
                </div>
                <div className="result-wrapper__map-location">{hotelsStore.searchedLocation}</div>
                <div
                  className={`result-wrapper__map-filters ${isSessionExpired ? 'disabled' : ''}`}
                  onClick={this.onMobileFilter}
                >
                  <FilterSvg />
                </div>
              </div>
              {matches && (
                <SearchPropertyName
                  disabled={isSessionExpired}
                  onSaveValue={this.onSearchSave}
                  title="search.property.name"
                  isMapView={isMapView}
                />
              )}
              <div className="result-wrapper__map-message">
                {isMapMessageShown && mapHotels?.length === zero ? (
                  <>
                    {!isFiltersEmpty(filtersStore) ? (
                      <EmptyFilterMessage onReset={() => this.onResetFilters(true)} />
                    ) : mapLoading ? null : (
                      <EmptySearchMessage label={<FormattedMessage id="no.properties" />} />
                    )}
                    <div
                      className="result-wrapper__map-message-close"
                      onClick={this.onCloseMessage}
                    >
                      <CloseSvg />
                    </div>
                  </>
                ) : null}
              </div>
              {mapHotels ? (
                <MapWrapper
                  bounds={bounds}
                  disabled={isSessionExpired}
                  hotels={mapHotels}
                  locations={mapHotels.map(({ location, hotelId, pricePerNight }) => ({
                    location,
                    id: hotelId,
                    pricePerNight,
                  }))}
                  nights={resultCounters.totalNights}
                  mapOptions={{ disableDefaultUI: true }}
                  withEvents={true}
                  selectedHotel={selectedHotel}
                  onSearchUpdate={this.onMapSearchUpdate}
                  setBounds={setBounds}
                />
              ) : null}
            </div>
          ) : (
            <div
              className={`result-wrapper__content-wrapper ${isMobileFilter ? 'is-mobile' : ''}`}
              style={{ paddingBottom: loadingMore ? '0px' : '250px' }}
            >
              {isSessionExpired && (
                <div className="result-wrapper__expired-message">
                  <SessionExpiredMessage onReset={() => window.location.reload()} />
                </div>
              )}
              {hotels && !hotels.length ? (
                !isFiltersEmpty(filtersStore) ? (
                  <EmptyFilterMessage onReset={() => this.onResetFilters()} />
                ) : (
                  <EmptySearchMessage
                    label={
                      lastSearchedLocation &&
                      lastSearchedLocation.locationType === LocationsTypes.Hotel ? (
                        <FormattedMessage id="sorry.could.not.find.the.specified.hotel" />
                      ) : (
                        <>
                          <FormattedMessage id="no.properties.found" />
                          {searchedLocation}
                          <FormattedMessage id="selected.dates" />
                        </>
                      )
                    }
                  />
                )
              ) : (
                <>
                  <div className="result-wrapper__top-section">
                    <p className="result-wrapper__top-header">
                      {label}
                      <FormattedMessage
                        id="properties.found"
                        values={{ count: hotelsStore?.counters?.totalFilteredHotels }}
                      />
                    </p>
                    <div className="result-wrapper__sort-select">
                      <span className="result-wrapper__sort-label">
                        <FormattedMessage id="sort.by" />:{' '}
                      </span>
                      <CustomSelect
                        disabled={isSessionExpired}
                        value={sortBy}
                        onChange={this.onSortChange}
                        options={this.getSortOptions()}
                      />
                    </div>
                  </div>
                  {!loadingFilters ? (
                    hotels.map((hotel: IHotel) => {
                      return (
                        <Responsive key={hotel.hotelId}>
                          <HotelCardComponent
                            disabled={isSessionExpired}
                            hotel={hotel}
                            nights={counters.totalNights}
                            suppliers={suppliers}
                            showOnMap={() =>
                              this.showMap(Map.getGoogleLocation(hotel.location), hotel)
                            }
                          />
                        </Responsive>
                      );
                    })
                  ) : (
                    <SkeletonContent label={''} />
                  )}
                  {loadingMore && <SkeletonContent label={''} isTitle={false} />}
                </>
              )}
            </div>
          )}
        </div>
        {mapLoading && isMapView && <SkeletonMap />}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    hotelsStore: state.hotelsStore,
    datesStore: state.datesStore,
    locationsStore: state.locationsStore,
    roomsStore: state.roomsStore,
    filtersStore: state.filtersStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setPageNumber: () => {
    dispatch(hotelsActions.setPageNumber());
  },
  resetHotels: () => {
    dispatch(resetHotels());
  },
  getHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    isLoadMore: boolean,
    isFiltersChange: boolean,
  ) => {
    dispatch(getHotels(location, startDate, endDate, rooms, isLoadMore, isFiltersChange));
  },
  getMapHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    hotelId: number = null,
  ) => {
    dispatch(getMapHotels(location, startDate, endDate, rooms, hotelId));
  },
  setBudget: (value: string[]) => {
    dispatch(filtersActions.setBudget(value));
  },
  setAmenities: (value: string[]) => {
    dispatch(filtersActions.setAmenities(value));
  },
  setAccommodationType: (value: string[]) => {
    dispatch(filtersActions.setAccommodationType(value));
  },
  setNeighbourhoods: (value: string[]) => {
    dispatch(filtersActions.setNeighbourhoods(value));
  },
  setPlaces: (value: string[]) => {
    dispatch(filtersActions.setPlaces(value));
  },
  setStarRating: (value: number[]) => {
    dispatch(filtersActions.setStarRating(value));
  },
  setDistance: (value: number) => {
    dispatch(filtersActions.setDistance(value));
  },
  setSortBy: (value: string) => {
    dispatch(filtersActions.setSortBy(value as SortTypes));
  },
  resetFilters: () => {
    dispatch(filtersActions.resetFilters());
  },
  setIsMapMessageShown: (shown: boolean) => {
    dispatch(hotelsActions.setIsMapMessageShown(shown));
  },
  validateLocation: (error: string) => {
    dispatch(locationActions.setError(error));
  },
  setBounds: (bounds: IBounds) => {
    dispatch(hotelsActions.setBounds(bounds));
  },
  setMapLoading: (isMapLoading: boolean) => {
    dispatch(hotelsActions.setMapLoading(isMapLoading));
  },
  setIsMapView: (isMapView: boolean) => {
    dispatch(hotelsActions.setIsMapView(isMapView));
  },
});

export const ResultWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(ResultWrapperComponent));
